import React, { useEffect, useState } from "react";

import type { FeatureFlagKeys, SampleKeys, SwitchKeys } from "~/types/main.d";

export type { FeatureFlagKeys, SampleKeys, SwitchKeys };

export type FlippySubject = "user" | "account";

export interface FlippingResult {
	sampled: boolean;
}

interface FetchFlipsProps {
	url: string;
	id: FeatureFlagKeys | SwitchKeys | SampleKeys;
	subject?: "user" | "account";
}

function fetchFlips({ url, id, subject }: FetchFlipsProps): Promise<FlippingResult> {
	let data = new FormData();

	data.append("id", String(id));
	if (subject) data.append("subject", subject);

	return fetch(url, {
		method: "POST",
		body: data,
		headers: {
			"x-client-session-id": window.CLIENT_SESSION_ID,
			"x-csrftoken": window.CSRF_TOKEN,
			"x-requested-with": "XMLHttpRequest",
		},
	})
		.then((res: Response) => {
			return res.json();
		})
		.catch(() => {
			return { sampled: false };
		});
}

// Switch

/**
 * Procedurally check if a switch has been enabled.
 *
 * There is no need to cache the result of this function; it's cached here.
 * Consequently multiple invocations will result in the same value even if the
 * value of the switch has changed in the database.
 */
async function fetchSwitch(id: SwitchKeys, subject?: FlippySubject) {
	let { switches } = window.INITIAL_STATE;

	const swtch = switches[id];
	if (swtch && subject) return swtch[subject];
	if (swtch) return swtch["enabled"];

	return fetchFlips({ url: "/flips/switch/", id, subject }).then(result => {
		const idResult = { enabled: false, user: false, account: false };

		if (subject) idResult[subject] = result.sampled;
		else idResult.enabled = result.sampled;
		window.INITIAL_STATE.switches[id] = idResult;

		return result.sampled;
	});
}

export function useSwitch(id: SwitchKeys, subject?: FlippySubject) {
	const [sampled, setSampled] = useState<boolean | undefined>(undefined);

	useEffect(() => {
		fetchSwitch(id, subject).then(setSampled);
	}, [id, subject]);

	return sampled;
}

interface WithSwitchProps {
	id: SwitchKeys;
	subject: FlippySubject;
	children: React.ReactElement;
}

export function WithSwitch(props: WithSwitchProps) {
	return useSwitch(props.id, props.subject) ? props.children : null;
}

// Feature
export async function fetchFeature(id: FeatureFlagKeys, subject: FlippySubject) {
	let { feature_flags: featureFlags } = window.INITIAL_STATE;

	const featureFlag = featureFlags[id];
	if (featureFlag) return featureFlag[subject];

	const result = await fetchFlips({ url: "/flips/feature/", id, subject });

	const idResult = { user: false, account: false };
	idResult[subject] = result.sampled;
	window.INITIAL_STATE.feature_flags[id] = idResult;

	return result.sampled;
}

export function useFeature(id: FeatureFlagKeys, subject: FlippySubject) {
	const [sampled, setSampled] = useState<boolean | undefined>(undefined);

	useEffect(() => {
		fetchFeature(id, subject).then(setSampled);
	}, [id, subject]);

	return sampled;
}

interface WithFeatureProps {
	id: FeatureFlagKeys;
	subject: FlippySubject;
	children: React.ReactElement;
}

export function WithFeature(props: WithFeatureProps) {
	return useFeature(props.id, props.subject) ? props.children : null;
}

export function WithoutFeature(props: WithFeatureProps) {
	return !useFeature(props.id, props.subject) ? props.children : null;
}

// Sample
async function fetchSample(id: SampleKeys, subject: FlippySubject) {
	let { samples } = window.INITIAL_STATE;

	const sample = samples[id];
	if (sample) return sample[subject];

	return fetchFlips({ url: "/flips/sample/", id, subject }).then(result => {
		const idResult = { user: false, account: false };
		idResult[subject] = result.sampled;
		// @ts-ignore
		window.INITIAL_STATE.samples[id] = idResult;

		return result.sampled;
	});
}

export function useSample(id: SampleKeys, subject: FlippySubject) {
	const [sampled, setSampled] = useState<boolean | undefined>(undefined);

	useEffect(() => {
		fetchSample(id, subject).then(setSampled);
	}, [id, subject]);

	return sampled;
}

interface WithSampleProps {
	id: SampleKeys;
	subject: FlippySubject;
	children: React.ReactElement;
}

export function WithSample(props: WithSampleProps) {
	return useSample(props.id, props.subject) ? props.children : null;
}
